import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "30";
const height = "40";
const viewBox = "0 0 30 40";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.orca}
            fillRule="evenodd"
            d="M29.665 0H18.66s.574 1.34 1.436 3.445v.096c2.966 6.985 9.569 22.2 9.569 22.105V0zm-8.134 25.55H17.13c0-.096-2.105-5.167-2.105-5.167H10.43v-.574a3.16 3.16 0 00-.096.21v-.21c.957-2.106 4.402-10.144 4.306-10.144.086 0 2.769 6.265 4.773 10.944l.682 1.592.436.967c.638 1.41 1.078 2.382 1 2.382zM10.335 20.02c-.028.067-.049.124-.066.172-.047.128-.07.192-.126.192h.192v-.364zM10.622 0H0v25.646S8.804 4.593 10.43.67c.096-.479.192-.67.192-.67zm16.842 39.904V40c.861 0 2.105-.191 2.393-2.297h-1.532c-.095.766-.478.862-.86.862v-.096c-.48 0-.958-.191-.958-1.914h3.445v-.383c0-3.54-1.34-4.115-2.392-4.115h-.096c-1.53.096-2.584 1.436-2.584 3.924 0 3.636 1.627 3.923 2.489 3.923h.095zm.957-4.593h-1.914c0-.574.096-1.722.957-1.818.479 0 .861.383.957 1.818zm-6.794 4.593V40h.383c1.626 0 2.2-1.722 2.2-4.02 0-2.2-.574-3.923-2.2-3.923-.091 0-.139 0-.184.01a.932.932 0 00-.2.086c-.382.096-.765.287-1.052.766v-3.35h-1.53v10.048h1.434v-.861h.096c.287.67.574.957 1.053 1.148zm.957-3.732c0 1.053-.192 2.393-.957 2.393v-.096c-.861 0-1.053-1.436-1.053-2.392 0-2.01.574-2.488 1.053-2.488v.095c.383 0 .957.383.957 2.488zm-6.986 3.637V40c1.723 0 2.488-1.627 2.488-3.732 0-2.297-.67-4.02-2.488-4.02v-.19c-1.818 0-2.584 1.722-2.584 3.827 0 2.297.766 3.924 2.584 3.924zm1.053-3.637c0 1.818-.383 2.393-1.053 2.393v-.192c-.478 0-.957-.287-.957-2.392s.479-2.488.957-2.488v.191c.574 0 1.053.479 1.053 2.488zm-6.125-.095c0 1.052-.191 2.488-1.052 2.488-.766-.096-.957-1.532-.957-2.488 0-2.01.574-2.393.957-2.488.478 0 1.052.382 1.052 2.488zm-.095-3.158c-.383-.479-.574-.67-.957-.766-.096-.096-.287-.096-.383-.096-1.723 0-2.201 2.106-2.201 3.924 0 2.2.67 4.019 2.201 4.019h.383c.383-.191.765-.67.957-1.148h.095v.86h1.436V29.57H10.43v3.35zm-3.732 6.794L4.306 29.569H2.68L.191 39.713h1.723l.478-1.914H4.593l.383 1.914H6.7zM4.21 36.077H2.679l.766-3.733v-.095l.766 3.828z"
            clipRule="evenodd"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g, &&& circle",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Adobe"
});
